import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/Button";
import { Select } from "@/components/Select";
import { Modal } from "@/components/Modal";

export interface ListPaginationProps {
    page: number;
    pageSize: number;
    total: number;
    onPage: (page: number) => void;
    onPageSize?: (size: number) => void;
    pageSizeOptions?: number[];
}

export const ListPagination = ({
    page,
    pageSize,
    total,
    onPage,
    onPageSize,
    pageSizeOptions = [10, 20, 50, 100, -1],
}: ListPaginationProps) => {
    const { t } = useTranslation("global");
    const pages =
        pageSize === -1 ? 1 : Math.max(1, Math.ceil(total / pageSize));
    const current = Math.max(1, Math.min(page, pages));
    const [jumpModalOpen, setJumpModalOpen] = useState(false);
    const numbers = Array.from(new Set([1, current, pages]))
        .filter((value) => value >= 1 && value <= pages)
        .sort((a, b) => a - b);
    const start = total
        ? pageSize === -1
            ? 1
            : (current - 1) * pageSize + 1
        : 0;
    const end = pageSize === -1 ? total : Math.min(current * pageSize, total);
    return (
        <nav
            aria-label={t("workspace.list.pagination_label")}
            className="flex flex-col gap-2 px-3 py-3 text-xs sm:flex-row sm:items-center sm:justify-between"
        >
            <div className="flex w-full items-center justify-between gap-2 sm:flex-1">
                <span
                    role="status"
                    aria-live="polite"
                    aria-atomic="true"
                    className="tabular-nums text-slate-600 dark:text-zinc-300"
                >
                    {t("workspace.list.pagination", { start, end, total })}
                </span>
                {onPageSize && (
                    <Select<number>
                        size="sm"
                        ariaLabel={t("workspace.list.page_size_label")}
                        value={pageSize}
                        options={Array.from(
                            new Set([...pageSizeOptions, pageSize])
                        ).map((value) => ({
                            value,
                            label:
                                value === -1
                                    ? t("pagination.all")
                                    : t("workspace.list.page_size", {
                                          count: value,
                                      }),
                        }))}
                        onChange={(size) => onPageSize(Number(size))}
                        className="sm:ml-auto"
                    />
                )}
            </div>
            <div className="flex w-full flex-wrap items-center justify-center gap-1.5 sm:w-auto sm:justify-end">
                <Button
                    variant="ghost"
                    size="sm"
                    onlyicon
                    icon="chevronLeft"
                    aria-label={t("workspace.list.previous")}
                    disabled={current === 1}
                    onClick={() => onPage(current - 1)}
                />
                {numbers.map((value, index) => (
                    <span
                        key={value}
                        className="inline-flex items-center gap-1.5"
                    >
                        {index > 0 && value > numbers[index - 1] + 1 && (
                            <Button
                                variant="ghost"
                                size="sm"
                                aria-label={t("pagination.select_page")}
                                onClick={() => {
                                    setJumpModalOpen(true);
                                }}
                            >
                                …
                            </Button>
                        )}
                        <Button
                            variant={current === value ? "primary" : "ghost"}
                            size="sm"
                            aria-label={t("workspace.list.page", {
                                page: value,
                            })}
                            aria-current={
                                current === value ? "page" : undefined
                            }
                            onClick={() => onPage(value)}
                        >
                            {value}
                        </Button>
                    </span>
                ))}
                <Button
                    variant="ghost"
                    size="sm"
                    onlyicon
                    icon="chevronRight"
                    aria-label={t("workspace.list.next")}
                    disabled={current === pages}
                    onClick={() => onPage(current + 1)}
                />
            </div>
            <Modal
                isOpen={jumpModalOpen}
                onClose={() => setJumpModalOpen(false)}
                title={t("pagination.select_page")}
                maxWidth="xs"
            >
                <div className="mb-3 text-center text-sm font-semibold text-slate-600 dark:text-zinc-300">
                    {t("pagination.page_status", { current, pages })}
                </div>
                <div className="grid max-h-72 grid-cols-4 gap-2 overflow-y-auto p-1">
                    {Array.from({ length: pages }, (_, index) => index + 1).map((pageNumber) => (
                        <button
                            key={pageNumber}
                            type="button"
                            className={`flex h-12 items-center justify-center rounded-xl border text-sm font-bold transition-colors ${pageNumber === current ? "border-primary bg-primary text-white shadow-sm" : "border-slate-200 bg-white text-slate-700 hover:border-primary/50 hover:bg-primary/5 hover:text-primary dark:border-white/10 dark:bg-dark-box dark:text-zinc-200 dark:hover:bg-primary/10"}`}
                            onClick={() => {
                                onPage(pageNumber);
                                setJumpModalOpen(false);
                            }}
                        >
                            {pageNumber}
                        </button>
                    ))}
                </div>
            </Modal>
        </nav>
    );
};
